import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import {
    AutomationFieldType,
    AutomationValueKind,
} from '@models/automation-rules';

import {
    parseOptionValues,
    serializeOptionValues,
} from './automation-option-value';
import { isAutomationWildcardKeyword } from './automation-wildcard';
import { primitiveText } from './string-utils';

/** The format an automation date value is stored and compared in. */
export const AUTOMATION_DATE_FORMAT = 'yyyy-MM-dd';

/** Invariant number: an optional sign and digits with at most one point. */
const NUMBER_PATTERN = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;

/** Invariant integer: an optional sign and digits only. */
const INTEGER_PATTERN = /^[+-]?\d+$/;

/** The range the api stores an integer field in, a signed 64 bit integer. */
const INTEGER_MIN = -(2n ** 63n);
const INTEGER_MAX = 2n ** 63n - 1n;

/** A canonical date, or an iso value whose date part is taken. */
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;

export function isSelectFieldType(type: AutomationFieldType): boolean {
    return (
        type === AutomationFieldType.SingleSelect ||
        type === AutomationFieldType.MultiSelect
    );
}

/**
 * Whether the field is the reason, the only list the story asks to be typed
 * into. The rule type model carries no field kind, so the api name is what
 * identifies it; a renamed field falls back to a plain dropdown.
 */
export function isSearchableField(fieldName: string): boolean {
    return fieldName.trim().toLowerCase() === 'reason';
}

export function isNumericFieldType(type: AutomationFieldType): boolean {
    return (
        type === AutomationFieldType.Number ||
        type === AutomationFieldType.Integer
    );
}

/**
 * Html input type a non select field is edited with.
 *
 * @param allowWildcard - Whether the field may be typed as the wildcard
 * keyword, which a numeric input refuses to hold.
 */
export function inputTypeOfFieldType(
    type: AutomationFieldType,
    allowWildcard = false,
): string {
    return isNumericFieldType(type) && !allowWildcard ? 'number' : 'text';
}

/**
 * Step the input of a field holds, so an integer is stepped in whole numbers.
 * `null` leaves the browser default, which a decimal number needs.
 */
export function stepOfFieldType(type: AutomationFieldType): number | null {
    return type === AutomationFieldType.Integer ? 1 : null;
}

export function isAutomationNumber(value: unknown): boolean {
    return NUMBER_PATTERN.test(primitiveText(value).trim());
}

/** Whether a value is an integer the api takes, so within its 64 bit range. */
export function isAutomationInteger(value: unknown): boolean {
    const text = primitiveText(value).trim();
    if (!INTEGER_PATTERN.test(text)) return false;

    const integer = BigInt(text);

    return integer >= INTEGER_MIN && integer <= INTEGER_MAX;
}

/**
 * A date value as a local date-only `Date`, so an editor shows the day the api
 * stores rather than the day the browser time zone shifts it to.
 */
export function parseAutomationDate(value: unknown): Date | null {
    const date = toDate(value);
    if (date) return Number.isNaN(date.getTime()) ? null : date;

    const parts = DATE_PATTERN.exec(primitiveText(value).trim());
    if (!parts) return null;

    const [year, month, day] = parts.slice(1, 4).map(Number);
    const parsed = new Date(year, month - 1, day);

    // A day that rolled over into the next month was never a date.
    return parsed.getMonth() === month - 1 && parsed.getDate() === day
        ? parsed
        : null;
}

/** A date value in the canonical format, or `''` when it is not a date. */
export function formatAutomationDate(value: unknown): string {
    const date = parseAutomationDate(value);
    if (!date) return '';

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * A number value without leading zeros, grouping or spaces. A value that is
 * not a number is kept as typed: the api rejects it with its own message.
 */
export function formatAutomationNumber(value: unknown): string {
    const text = primitiveText(value).trim();
    if (text === '') return '';

    return isAutomationNumber(text) ? String(Number(text)) : text;
}

/**
 * An integer value without a sign it does not need, leading zeros or spaces,
 * so duplicate detection agrees with the api. A fraction is not an integer, so
 * it is kept as typed rather than rounded: the validator refuses it and the
 * api would too. The digits are trimmed as text rather than through a
 * `number`, which loses precision past its safe integer range while the api
 * stores a 64 bit integer.
 */
export function formatAutomationInteger(value: unknown): string {
    const text = primitiveText(value).trim();
    if (text === '' || !isAutomationInteger(text)) return text;

    const negative = text.startsWith('-');
    const digits = text.replace(/^[+-]/, '').replace(/^0+(?=\d)/, '');

    return negative && digits !== '0' ? `-${digits}` : digits;
}

/**
 * What the api stores for a field, out of the value of its editor.
 *
 * @param valueKind - What the option values of the field mean. A selection of
 * names is stored as a JSON array, because a name may contain the comma a
 * selection of ids is separated by.
 */
export function serializeAutomationValue(
    type: AutomationFieldType,
    value: unknown,
    valueKind?: AutomationValueKind | null,
): string {
    if (Array.isArray(value)) {
        return serializeOptionValues(
            value as (string | number | null | undefined)[],
            valueKind,
        );
    }

    switch (type) {
        case AutomationFieldType.Date:
            return formatAutomationDate(value);
        case AutomationFieldType.Number:
            return formatAutomationNumber(value);
        case AutomationFieldType.Integer:
            return formatAutomationInteger(value);
        default:
            return primitiveText(value).trim();
    }
}

/** The editor value of what the api stores for a field. */
export function parseAutomationValue(
    type: AutomationFieldType,
    value: unknown,
    valueKind?: AutomationValueKind | null,
): string | string[] | Date | null {
    switch (type) {
        case AutomationFieldType.Date:
            return parseAutomationDate(value);
        case AutomationFieldType.Number:
            return formatAutomationNumber(value);
        case AutomationFieldType.Integer:
            return formatAutomationInteger(value);
        case AutomationFieldType.MultiSelect:
            return parseOptionValues(primitiveText(value), valueKind);
        default:
            return primitiveText(value);
    }
}

/** The value an empty editor of a field holds. */
export function emptyAutomationValue(
    type: AutomationFieldType,
): string | number[] | null {
    switch (type) {
        case AutomationFieldType.MultiSelect:
            return [];
        case AutomationFieldType.FreeText:
        case AutomationFieldType.Number:
        case AutomationFieldType.Integer:
            return '';
        default:
            return null;
    }
}

/**
 * A validator of a numeric input: an empty input is left to the required
 * validator, and the wildcard keyword is a value of its own rather than a
 * number, so both pass before the shape of the number is looked at.
 *
 * @param allowWildcard - Whether the keyword standing for all values of the
 * field passes as well, which is how a typed input expresses the wildcard.
 */
function automationNumericValidator(
    isValid: (text: string) => boolean,
    errorKey: string,
    allowWildcard: boolean,
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const text = primitiveText(control.value).trim();
        if (text === '') return null;
        if (allowWildcard && isAutomationWildcardKeyword(text)) return null;

        return isValid(text) ? null : { [errorKey]: true };
    };
}

/** @param allowWildcard - As in {@link automationNumericValidator}. */
export function automationNumberValidator(allowWildcard = false): ValidatorFn {
    return automationNumericValidator(
        isAutomationNumber,
        'automationNumber',
        allowWildcard,
    );
}

/** @param allowWildcard - As in {@link automationNumericValidator}. */
export function automationIntegerValidator(allowWildcard = false): ValidatorFn {
    return automationNumericValidator(
        isAutomationInteger,
        'automationInteger',
        allowWildcard,
    );
}

export function automationDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (control.value == null || control.value === '') return null;

        return parseAutomationDate(control.value)
            ? null
            : { automationDate: true };
    };
}

/** A `Date`, or the moment the material datepicker emits, if either. */
function toDate(value: unknown): Date | null {
    if (value instanceof Date) return value;

    const candidate = value as { toDate?: () => Date } | null;
    return typeof candidate?.toDate === 'function' ? candidate.toDate() : null;
}

/**
 * The validators a field needs on top of the required one, if any.
 *
 * @param allowWildcard - As in {@link automationNumberValidator}.
 */
export function automationValueValidators(
    type: AutomationFieldType,
    allowWildcard = false,
): ValidatorFn[] {
    switch (type) {
        case AutomationFieldType.Number:
            return [automationNumberValidator(allowWildcard)];
        case AutomationFieldType.Integer:
            return [automationIntegerValidator(allowWildcard)];
        case AutomationFieldType.Date:
            return [automationDateValidator()];
        default:
            return [];
    }
}
